import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Settings as SettingsIcon,
  Palette,
  Clock,
  CreditCard,
  Briefcase,
  Users as UsersIcon,
  ShieldHalf,
  BookOpen,
  Globe,
  Shield,
  Building2,
  Zap,
  Ticket,
  Puzzle,
} from 'lucide-react';
import { cn } from '@/utils/cn';
import { useAuth } from '@/lib/auth';
import { useActiveClinic } from '@/lib/activeClinic';
import {
  INTEGRATIONS_PERMISSION_RESOURCES,
  type PermissionResource,
} from '@/lib/permissions';

const INTEGRATION_PATH_PREFIXES = [
  '/settings/integrations',
  '/settings/payments',
  '/settings/questionnaires',
  '/settings/communicate',
  '/settings/sms',
  '/settings/sygeforsikring',
];

interface SubNavItem {
  to: string;
  i18nKey: string;
  icon: typeof SettingsIcon;
  permissionResource?: PermissionResource;
}

interface SubNavGroup {
  i18nKey: string;
  items: SubNavItem[];
}

const SUB_NAV_GROUPS: SubNavGroup[] = [
  {
    i18nKey: 'settings.nav_groups.clinic',
    items: [
      {
        to: '/settings/general',
        i18nKey: 'settings.nav.general',
        icon: SettingsIcon,
        permissionResource: 'settings_general',
      },
      {
        to: '/settings/branding',
        i18nKey: 'settings.nav.branding',
        icon: Palette,
        permissionResource: 'settings_general',
      },
      {
        to: '/settings/hours',
        i18nKey: 'settings.nav.hours',
        icon: Clock,
        permissionResource: 'settings_general',
      },
      {
        to: '/settings/services',
        i18nKey: 'settings.nav.services',
        icon: Briefcase,
        permissionResource: 'settings_general',
      },
      {
        to: '/settings/online-booking',
        i18nKey: 'settings.nav.online_booking',
        icon: Globe,
        permissionResource: 'online_booking',
      },
    ],
  },
  {
    i18nKey: 'settings.nav_groups.staff',
    items: [
      {
        to: '/settings/users',
        i18nKey: 'settings.nav.users',
        icon: UsersIcon,
        permissionResource: 'settings_users',
      },
      {
        to: '/settings/roles',
        i18nKey: 'settings.nav.roles',
        icon: ShieldHalf,
        permissionResource: 'settings_roles',
      },
    ],
  },
  {
    i18nKey: 'settings.nav_groups.finance',
    items: [
      {
        to: '/settings/billing',
        i18nKey: 'settings.nav.billing',
        icon: CreditCard,
        permissionResource: 'settings_billing',
      },
      {
        to: '/settings/punch-card-templates',
        i18nKey: 'settings.nav.punch_card_templates',
        icon: Ticket,
        permissionResource: 'punch_cards',
      },
    ],
  },
  {
    i18nKey: 'settings.nav_groups.clinical',
    items: [
      {
        to: '/settings/journal-templates',
        i18nKey: 'settings.nav.journal_templates',
        icon: BookOpen,
        permissionResource: 'journal',
      },
      {
        to: '/settings/dental-shortcuts',
        i18nKey: 'settings.nav.dental_shortcuts',
        icon: Zap,
        permissionResource: 'dental',
      },
    ],
  },
  {
    i18nKey: 'settings.nav_groups.organization',
    items: [
      {
        to: '/settings/integrations',
        i18nKey: 'settings.nav.integrations',
        icon: Puzzle,
        // Hub visibility: any integration permission (see filter below)
        permissionResource: 'settings_general',
      },
      {
        to: '/settings/clinics',
        i18nKey: 'settings.nav.clinics',
        icon: Building2,
        permissionResource: 'multi_clinic',
      },
      {
        to: '/settings/audit-logs',
        i18nKey: 'settings.nav.audit_logs',
        icon: Shield,
        permissionResource: 'audit_logs',
      },
    ],
  },
];

/**
 * Two-column settings shell mirroring v1's `SettingsLayout`
 * (Clinio/src/components/SettingsLayout.tsx): rounded-xl side-nav with active
 * indigo state, scrollable content area on the right.
 */
export function SettingsLayout() {
  const { t } = useTranslation();
  const location = useLocation();
  const { hasPermission } = useAuth();
  const { activeClinicId } = useActiveClinic();

  const visibleGroups = SUB_NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => {
      if (!activeClinicId) return false;
      if (item.to === '/settings/integrations') {
        return INTEGRATIONS_PERMISSION_RESOURCES.some((r) =>
          hasPermission(activeClinicId, r, 'view'),
        );
      }
      if (!item.permissionResource) return true;
      return hasPermission(activeClinicId, item.permissionResource, 'view');
    }),
  })).filter((group) => group.items.length > 0);

  const isIntegrationsSection = INTEGRATION_PATH_PREFIXES.some(
    (prefix) =>
      location.pathname === prefix || location.pathname.startsWith(`${prefix}/`),
  );

  return (
    <div className="pb-12">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
          {t('settings.title')}
        </h1>
        <p className="mt-1 text-sm text-gray-700 dark:text-gray-400">{t('settings.subtitle')}</p>
      </div>

      <div className="flex flex-col gap-8 md:flex-row">
        <nav className="w-full shrink-0 space-y-6 md:w-64">
          {visibleGroups.map((group) => (
            <div key={group.i18nKey}>
              <h2 className="mb-2 px-4 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {t(group.i18nKey)}
              </h2>
              <div className="space-y-1">
                {group.items.map(({ to, i18nKey, icon: Icon }) => (
                  <NavLink
                    key={to}
                    to={to}
                    className={({ isActive }) => {
                      const active =
                        to === '/settings/integrations' ? isIntegrationsSection : isActive;
                      return cn(
                        'flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-colors duration-200',
                        active
                          ? 'bg-white text-indigo-600 shadow-sm dark:bg-gray-800 dark:text-indigo-400'
                          : 'text-gray-700 hover:bg-white hover:text-indigo-600 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-indigo-400',
                      );
                    }}
                  >
                    {({ isActive }) => {
                      const active =
                        to === '/settings/integrations' ? isIntegrationsSection : isActive;
                      return (
                        <>
                          <Icon
                            className={cn(
                              'h-5 w-5',
                              active
                                ? 'text-indigo-600 dark:text-indigo-400'
                                : 'text-gray-400 dark:text-gray-500',
                            )}
                          />
                          {t(i18nKey)}
                        </>
                      );
                    }}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="min-w-0 flex-1">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
